/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { get } from 'lodash';
import componentIsResizable from './componentIsResizable';
import shouldWrapChildInRow from './shouldWrapChildInRow';
import newComponentFactory from './newComponentFactory';
import getComponentWidthFromDrop from './getComponentWidthFromDrop';

import { FLOATING_TYPE, ROW_TYPE, TABS_TYPE, TAB_TYPE } from './componentTypes';
import { position } from 'polished';
import { GRID_DEFAULT_CHART_WIDTH } from './constants';

export default function newEntitiesFromDrop({ dropResult, layout }) {
  const { dragging, destination } = dropResult;
  const dragType = dragging.type;
  const dropEntity = layout[destination.id];
  const dropType = dropEntity.type;
  //check if floating compoennt
  const wrapChildInFloating = dragging.meta?dragging.meta.floating:false;
  let PositionFloating = {}
  let tabsAdded = false;
  let newDropChild;
  // console.log(dragging.meta)
  // condition if floatind is on get cordinates and create dragging component else only create dragging compoennt
  if(wrapChildInFloating){
    PositionFloating['top'] = dragging.meta.top;
    PositionFloating['left'] = dragging.meta.left;
    delete dragging.meta.top;
    delete dragging.meta.left;
    newDropChild = newComponentFactory(dragType, dragging.meta);
    // newDropChild.parents = ['ROOT_ID','GRID_ID'];
    newDropChild.parents = (dropEntity.parents || []).concat(dropEntity.id); // Inherit component hierarchy from drop entity
  }
  else{
    newDropChild = newComponentFactory(dragType, dragging.meta);
    newDropChild.parents = (dropEntity.parents || []).concat(dropEntity.id);
  }

  if (componentIsResizable(dragging)) {
    newDropChild.meta.width = // don't set a 0 width
      getComponentWidthFromDrop({ dropResult, layout }) || undefined;
  }

  const wrapChildInRow = shouldWrapChildInRow({
    parentType: dropType,
    childType: dragType,
  });

  const newEntities = {
    [newDropChild.id]: newDropChild,
  };
  /**
   * conditions;
   * 1.if droping compoennt is floating yes : - crate floating compoennt and asign its positions
   * and asign parent and children to floting compoennt
   * subconditions; 
   *    a.if floating is droped inside tab
   */
  if(wrapChildInFloating && dropType !== FLOATING_TYPE){
    const floatWrapper = newComponentFactory(FLOATING_TYPE);
    floatWrapper.meta = {};
    floatWrapper.meta['top'] = PositionFloating.top;
    floatWrapper.meta['left'] = PositionFloating.left;
    floatWrapper.meta['width'] = GRID_DEFAULT_CHART_WIDTH;
    floatWrapper.children = [newDropChild.id];
    floatWrapper.parents = ['ROOT_ID','GRID_ID'];
    for (const ele of dropEntity.parents){
      if (ele.includes('TABS')){
        floatWrapper.parents = floatWrapper.parents.concat(ele);
        tabsAdded = true;
      }
    }
    if(dropEntity.type === TAB_TYPE){
      floatWrapper.parents = floatWrapper.parents.concat(dropEntity.id);
    }
    if(dropEntity.type === TABS_TYPE){
      // If dropping an element directly into tabs, add the active tab id to parents
      // Get the active tab id from the tabs component
      const activeTabId = get(dropEntity, `children.[${destination.index - 1}]`, null);
      if (activeTabId){
        floatWrapper.parents = floatWrapper.parents.concat(dropEntity.id, activeTabId); // add the active tab id to parents
      }
    }
    newEntities[floatWrapper.id] = floatWrapper;
    newDropChild.parents = floatWrapper.parents.concat(floatWrapper.id);
    if (dragType === TABS_TYPE) {
      // create a new tab component for floating Type
      const tabChild = newComponentFactory(TAB_TYPE);
      tabChild.parents = (newDropChild.parents || []).concat(newDropChild.id);
      newDropChild.children = [tabChild.id];
      newEntities[tabChild.id] = tabChild;
    }
    newDropChild = floatWrapper; 
  }
  else if (wrapChildInRow) {
    const rowWrapper = newComponentFactory(ROW_TYPE);
    rowWrapper.children = [newDropChild.id];
    rowWrapper.parents = (dropEntity.parents || []).concat(dropEntity.id);
    newEntities[rowWrapper.id] = rowWrapper;
    newDropChild.parents = rowWrapper.parents.concat(rowWrapper.id);
    newDropChild = rowWrapper;
  } else if (dragType === TABS_TYPE) {
    // create a new tab component
    const tabChild = newComponentFactory(TAB_TYPE);
    tabChild.parents = (dropEntity.parents || []).concat(dropEntity.id);
    newDropChild.children = [tabChild.id];
    newEntities[tabChild.id] = tabChild;
  }
  else if(wrapChildInFloating && dropType === FLOATING_TYPE){
    return;
  }
  
  if(wrapChildInFloating && !tabsAdded){
    if (dropEntity.type === TABS_TYPE) {
      // If dropping an element directly into tabs, add new drop entity as children to active tab
      // Get the active tab id from the tabs component
      const activeTabId = get(dropEntity, `children.[${destination.index - 1}]`, null);
      if (activeTabId){
        const activeTab = layout[activeTabId];
        newEntities[activeTabId] = {
          ...activeTab,
          children: [...activeTab.children, newDropChild.id], // add new component as children of active tab
        };
        return newEntities;
      }
    } else {
      const nextDropChildren = [...layout['GRID_ID'].children];
      nextDropChildren.push(newDropChild.id);
  
      newEntities['GRID_ID'] = {
        ...layout['GRID_ID'],
        children: nextDropChildren,
      };
      return newEntities;
    }
  } else {
    const nextDropChildren = [...dropEntity.children];
    nextDropChildren.splice(destination.index, 0, newDropChild.id);

    newEntities[destination.id] = {
      ...dropEntity,
      children: nextDropChildren,
    };
    return newEntities;
  }
}
